/**
 * Dark-theme Markdown rendering for the manuscript viewer.
 *
 * Wraps react-markdown with KaTeX (remark-math + rehype-katex) and binds the
 * component overrides to the viewer's color palette. Sandboxed by react-
 * markdown's default safe rendering: no raw HTML, no `javascript:` URLs
 * (links go through `SafeLink`).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type React from "react";
import type ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import { SafeLink } from "../SafeLink.js";
import type { ManuscriptViewerTheme } from "./types.js";

export const REMARK_PLUGINS = [remarkMath];

export const REHYPE_PLUGINS: React.ComponentProps<typeof ReactMarkdown>["rehypePlugins"] = [
  [rehypeKatex, { output: "html", throwOnError: false, strict: "ignore", trust: false }],
];

export function buildMarkdownComponents(
  theme: ManuscriptViewerTheme,
): React.ComponentProps<typeof ReactMarkdown>["components"] {
  return {
    p: ({ children }) => (
      <p style={{ margin: "8px 0", fontSize: 14, lineHeight: 1.75, color: theme.text }}>
        {children}
      </p>
    ),
    h1: ({ children }) => (
      <h1
        style={{
          fontSize: 22,
          fontWeight: 300,
          color: theme.bright,
          fontFamily: theme.serif,
          margin: "24px 0 8px",
          letterSpacing: 0,
          lineHeight: 1.3,
        }}
      >
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: theme.accent,
          fontFamily: theme.serif,
          letterSpacing: 0,
          margin: "20px 0 6px",
        }}
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: theme.accentSoft,
          fontFamily: theme.serif,
          margin: "16px 0 4px",
        }}
      >
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4
        style={{
          color: theme.bright,
          fontFamily: theme.serif,
          fontSize: 13,
          fontWeight: 600,
          margin: "12px 0 4px",
        }}
      >
        {children}
      </h4>
    ),
    strong: ({ children }) => (
      <strong style={{ color: theme.bright, fontWeight: 600 }}>{children}</strong>
    ),
    em: ({ children }) => <em style={{ color: theme.text, fontStyle: "italic" }}>{children}</em>,
    code: ({ children }) => (
      <code
        style={{
          background: theme.border,
          padding: "1px 6px",
          borderRadius: 3,
          fontSize: 12,
          fontFamily: theme.mono,
          color: theme.accent,
        }}
      >
        {children}
      </code>
    ),
    pre: ({ children }) => (
      <pre
        style={{
          background: theme.void,
          border: `1px solid ${theme.border}`,
          borderRadius: 4,
          padding: "12px 16px",
          margin: "10px 0",
          overflow: "auto",
          fontSize: 12,
          lineHeight: 1.5,
          fontFamily: theme.mono,
          color: theme.accentSoft,
        }}
      >
        {children}
      </pre>
    ),
    ul: ({ children }) => (
      <ul
        style={{
          margin: "8px 0 8px 20px",
          fontSize: 14,
          lineHeight: 1.7,
          color: theme.text,
        }}
      >
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol
        style={{
          margin: "8px 0 8px 24px",
          fontSize: 14,
          lineHeight: 1.7,
          color: theme.text,
        }}
      >
        {children}
      </ol>
    ),
    li: ({ children }) => <li style={{ margin: "2px 0" }}>{children}</li>,
    blockquote: ({ children }) => (
      <blockquote
        style={{
          borderLeft: `3px solid ${theme.border}`,
          paddingLeft: 16,
          margin: "10px 0",
          color: theme.muted,
          fontStyle: "italic",
          fontSize: 14,
          lineHeight: 1.7,
        }}
      >
        {children}
      </blockquote>
    ),
    hr: () => (
      <hr style={{ border: "none", borderTop: `1px solid ${theme.border}`, margin: "16px 0" }} />
    ),
    a: ({ href, children }) => (
      <SafeLink
        href={typeof href === "string" ? href : ""}
        style={{ color: theme.accentSoft, textDecoration: "underline" }}
      >
        {children}
      </SafeLink>
    ),
    table: ({ children }) => (
      <div style={{ overflowX: "auto", margin: "10px 0" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 12,
            fontFamily: theme.mono,
            color: theme.text,
          }}
        >
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th
        style={{
          padding: "4px 8px",
          borderBottom: `1px solid ${theme.border}`,
          color: theme.accentSoft,
          textAlign: "left",
          fontWeight: 600,
        }}
      >
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td
        style={{
          padding: "4px 8px",
          borderBottom: `1px solid ${theme.border}`,
          color: theme.text,
          textAlign: "left",
        }}
      >
        {children}
      </td>
    ),
  };
}
